import React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/material';

type PillVariant = 'filled' | 'outlined';
type PillSize = 'sm' | 'md' | 'lg';

export interface PillProps {
  label: string;
  active?: boolean;
  size?: PillSize;
  variant?: PillVariant;
  icon?: React.ReactNode;
  iconPosition?: 'start' | 'end';
}

const SegmentedPillWrapper = styled(Box)({
  fontWeight: 600,
  borderRadius: 999,
  height: 'fit-content',
  textAlign: 'center' as const,
  cursor: 'pointer',
  transition: 'background 0.2s, color 0.2s',
  userSelect: 'none' as const,
  width: 'fit-content',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 6,

  '&:hover': {
    backgroundColor: '#e3f1ff'
  }
});

// Style helpers
const sizeStyles = {
  sm: { fontSize: 12, padding: '4px 12px' },
  md: { fontSize: 14, padding: '8px 16px' },
  lg: { fontSize: 16, padding: '12px 24px' }
};

const getBackgroundColor = (
  active: boolean,
  variant: PillVariant,
  size: PillSize
): string => {
  if (!active) return '#transparent';

  if (variant === 'filled') {
    const activeBackgrounds: Record<PillSize, string> = {
      sm: '#e3f1ff',
      md: '#cbe7ff',
      lg: '#9fd6ff'
    };
    return activeBackgrounds[size];
  }

  return '#EBF5FF';
};

const getPillStyles = (
  active: boolean,
  variant: PillVariant,
  size: PillSize
) => {
  const { fontSize, padding } = sizeStyles[size];

  return {
    background: getBackgroundColor(active, variant, size),
    color: active ? '#2196f3' : '#5D7083',
    fontSize,
    padding,
    boxShadow: active ? '0px 2px 1px 0px rgba(48, 54, 60, 0.1)' : 'none'
  };
};

const SegmentedPill = React.memo(
  ({
    label,
    active = false,
    size = 'md',
    variant = 'outlined',
    icon,
    iconPosition = 'start'
  }: PillProps) => {
    const pillStyle = getPillStyles(active, variant, size);

    return (
      <SegmentedPillWrapper sx={pillStyle}>
        {icon && iconPosition === 'start' && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center'
            }}>
            {icon}
          </Box>
        )}
        <Typography
          sx={{
            fontWeight: 'inherit',
            fontSize: 'inherit',
            color: 'inherit',
            textAlign: 'center'
          }}>
          {label}
        </Typography>
        {icon && iconPosition === 'end' && (
          <Box sx={{ display: 'flex', alignItems: 'center' }}>{icon}</Box>
        )}
      </SegmentedPillWrapper>
    );
  }
);

export default SegmentedPill;
